/**
 * Question image rendering + preload.
 *
 * Used by duel/solo (match.js), live trivia (live_room.js), and the
 * post-game per-question breakdown (match_result.js).
 *
 * Goals during gameplay:
 *   - **Always render the WHOLE image at its native aspect ratio.** We do
 *     not impose a slot aspect ratio. Flags come in many shapes (Kazakhstan
 *     2:1, Norway 22:16, Nepal vertical, etc.) and any fixed slot either
 *     letterboxes (wastes screen real estate) or — if a CDN ever serves
 *     a stale/wrong-sized PNG — visually crops parts of the flag away.
 *     Letting `<img>` size itself by its natural ratio kills both bugs.
 *   - **Fill the available width** so the image is the dominant visual on
 *     phone and on tablet/desktop alike, capped so it doesn't dominate
 *     wide viewports.
 *   - **No layout shift on subsequent questions** via preloading. The
 *     server emits `question.preload` events during reveal phase carrying
 *     only the next image's URL (never the prompt). We force a fetch into
 *     the browser cache here; the actual `<img>` tag in paintPrompt then
 *     renders instantly off the cached bytes (so it shows up sized
 *     correctly the moment the slot mounts, no reflow). The first question
 *     is the only one that can briefly grow the slot as bytes arrive —
 *     acceptable trade-off vs. cropping the flag.
 *   - **Bandwidth manners** via lazy loading on result/replay screens
 *     where users scroll through historical questions.
 */

import { escape } from '../components/form.js';

/**
 * Render an image card slot for the active question. Returns empty string
 * when `imageUrl` is falsy so layout reflows tightly for text-only
 * questions (no empty box of dead space).
 *
 * Options:
 *   - lazy: `true` for non-current questions (e.g. result screen breakdown).
 *           Defaults to `false` for gameplay where we want eager loading.
 *   - maxHeightPx: cap on the image height in px. 280 (default) leaves
 *                  room for the prompt + 2 rows of choice tiles (the
 *                  picture-question 2×2 grid layout) above the fold on
 *                  a phone while keeping the image meaningfully large.
 *                  Portrait shots are the main constraint — landscape
 *                  images usually hit the max-width cap first and never
 *                  reach this limit.
 */
export function questionImageHtml(imageUrl, opts = {}) {
  if (!imageUrl) return '';
  const lazy = opts.lazy ? 'loading="lazy" decoding="async"' : 'decoding="async"';
  const maxH = opts.maxHeightPx || 280;
  // Retry-once on load failure. flagcdn.com / S3 occasionally returns a
  // connection reset under burst load (live trivia start). One re-fetch
  // (cache-busting query string) almost always succeeds and the browser
  // caches the bytes — so a transient blip becomes a ~500ms render delay
  // instead of a permanently broken image.
  //
  // ⚠️ The inner `"` characters around the fallback span's style must be
  // emitted as `&quot;` HTML entities, not raw `"`. This entire string is
  // dropped into `onerror="..."`, so a raw `"` here terminates the HTML
  // attribute early — which causes the browser to drop the <img>'s OWN
  // style attribute (the max-width / max-height caps) and render the
  // source image at full natural size, overflowing the viewport. We
  // tripped that exact bug before — keep the entities.
  const retryHandler = (
    "if(!this.dataset.r){this.dataset.r='1';" +
    "var u=new URL(this.src);u.searchParams.set('cb','1');" +
    "setTimeout(function(){this.src=u.toString();}.bind(this),400);}" +
    "else{this.style.display='none';" +
    "this.parentElement.innerHTML='<span style=&quot;font-size:11px;color:var(--mute);padding:18px;&quot;>Image unavailable</span>';}"
  );
  // Approach: NO fixed-aspect slot. The wrapper just centers and caps
  // width; the <img> renders at its native ratio (capped by max-width and
  // max-height, with width/height: auto so the browser preserves the ratio
  // automatically). Result: every flag fills as much screen as it can at
  // its own shape. A 2:1 Kazakhstan flag, a 22:16 Norway flag, and a
  // vertical Nepal flag all render correctly — no letterboxing, no chance
  // of partial-flag-due-to-slot-mismatch bugs.
  //
  // No background / border-radius / overflow-clip on the wrapper anymore:
  // those only mattered when we had a slot that was bigger than the image.
  // Now the wrapper IS the image, so the rounded border lives on the <img>.
  return `
    <div style="
      display:flex;
      justify-content:center;
      align-items:flex-start;
      width:100%;
      margin:0 auto 10px;
    ">
      <img
        src="${escape(imageUrl)}"
        alt=""
        ${lazy}
        onerror="${retryHandler}"
        style="
          max-width:100%;
          max-height:${maxH}px;
          width:auto;
          height:auto;
          display:block;
          border-radius:12px;
        "
      />
    </div>
  `;
}

/**
 * Force the browser to fetch + cache an image. Uses an off-DOM `Image()`
 * because it's simpler than a `<link rel=preload>` tag for one-shot URLs
 * and works equally well across browsers. No-op if URL is falsy or the
 * cache already has it (browser handles dedupe).
 */
export function preloadImage(imageUrl) {
  if (!imageUrl) return;
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = imageUrl;
  } catch {
    // Silently ignore — preload is a best-effort optimisation.
  }
}
